import type { ApolloError } from '@frameio/graphql-client';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useTrack } from '@frameio/shared/segment/hooks';
import { reportError } from '@frameio/telemetry';
import { noop, useToast } from '@frameio/vapor';
import type { NotificationsActionContext } from '../components/NotificationCell/types';
import { Toast, TOAST_OPTIONS } from '../components/Toast';
import type {
  EnableProjectNotificationsMutationOptions,
  EnableProjectNotificationsMutationVariables,
} from '../gql';
import {
  useDisableProjectNotificationsMutation,
  useEnableProjectNotificationsMutation,
  useProjectNotificationsStatusFragment,
} from '../gql';
import TEXT from './generated/TEXT/useToggleProjectNotifications';

TEXT.DEFINE({
  /** Toast message shown after toggling project notifications */
  title: (hasNotificationsEnabled: boolean | null | undefined): string => {
    const notificationsEnabled = !!hasNotificationsEnabled;
    return notificationsEnabled
      ? `Project notifications disabled. You'll only be notified when @mentioned or assigned.`
      : 'Project notifications enabled.';
  },
  /** Error message when project notification toggle fails */
  error: (hasNotificationsEnabled: boolean | null | undefined): string => {
    const notificationsEnabled = !!hasNotificationsEnabled;
    return notificationsEnabled
      ? `Project notifications can't be disabled at this time.`
      : `Project notifications can't be enabled at this time.`;
  },
  /** Message when attempting to disable already disabled notifications */
  notificationsAlreadyDisabled: 'Project notifications already disabled.',
});

type ToggleProjectNotifications = {
  projectId: string | undefined;
  context?: NotificationsActionContext;
};

type ToggleProjectNotificationsArgs = {
  variables: EnableProjectNotificationsMutationVariables;
} & EnableProjectNotificationsMutationOptions;

type OnCompletedCallback = () => void;

type MutationArgs = {
  force?: boolean;
  onCompleted?: OnCompletedCallback;
  shouldShowToast?: boolean;
};

/**
 * @description The `useToggleProjectNotifications` hook returns the
 * `toggleProjectNotificationsMutation` mutation so that notifications for an
 * inividual Project can be turned on or off.
 *
 * @param {string} projectId - the projectId to use to turn notifications on or
 * off.
 *
 * @returns An object containing mutation functions for managing project notifications:
 * - `toggleProjectNotificationsMutation`: A function to toggle notifications on or off.
 * - `disableProjectNotificationsMutation`: A function to disable notifications for the project.
 * - `enableProjectNotificationsMutation`: A function to enable notifications for the project.
 */
export const useToggleProjectNotifications = ({
  projectId,
  context,
}: ToggleProjectNotifications) => {
  const accountId = useActiveAccountId();

  const { addToast } = useToast();

  const [disableProjectNotifications] =
    useDisableProjectNotificationsMutation();

  const [enableProjectNotifications] = useEnableProjectNotificationsMutation();

  const track = useTrack();

  const project = useProjectNotificationsStatusFragment({
    from: {
      __typename: 'Project',
      id: projectId ?? '',
    },
  });

  if (!project || !projectId)
    return {
      disableProjectNotificationsMutation: noop,
      enableProjectNotificationsMutation: noop,
      toggleProjectNotificationsMutation: noop,
    };

  const mutationArgs = ({
    hasNotificationsEnabled,
    onCompleted,
    shouldShowToast = true,
  }: {
    hasNotificationsEnabled: boolean;
    onCompleted?: OnCompletedCallback;
    shouldShowToast?: boolean;
  }): ToggleProjectNotificationsArgs => {
    const mutation = hasNotificationsEnabled
      ? 'disableProjectNotifications'
      : 'enableProjectNotifications';

    return {
      variables: {
        input: {
          accountId,
          projectId,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        [mutation]: {
          __typename: 'ToggleProjectNotificationsPayload',
          project: {
            id: projectId,
            notificationsEnabled: !hasNotificationsEnabled,
            hasProjectNotificationsOverride: true,
            __typename: 'Project',
          },
        },
      },
      onCompleted: () => {
        if (shouldShowToast) {
          addToast(
            <Toast title={TEXT.title(hasNotificationsEnabled)} />,
            TOAST_OPTIONS
          );
        }
        track('project-notifications-updated', {
          project_id: projectId,
          notifications_enabled: !hasNotificationsEnabled,
          ...(context ? { context } : {}),
        });
        onCompleted?.();
      },
      onError: (error: ApolloError) => {
        addToast(
          <Toast
            title={TEXT.error(hasNotificationsEnabled)}
            variant={'error'}
          />,
          TOAST_OPTIONS
        );
        reportError(error, {
          message: 'Failed to toggle project notifications',
        });
      },
    };
  };

  const hasNotificationsEnabled = !!project?.data?.notificationsEnabled;

  const toggleProjectNotificationsMutation = () => {
    const toggleProjectNotifications = hasNotificationsEnabled
      ? (args: ToggleProjectNotificationsArgs) =>
          disableProjectNotifications(args)
      : (args: ToggleProjectNotificationsArgs) =>
          enableProjectNotifications(args);

    return toggleProjectNotifications(
      mutationArgs({ hasNotificationsEnabled })
    );
  };

  const disableProjectNotificationsMutation = ({
    force,
    onCompleted,
    shouldShowToast,
  }: MutationArgs) => {
    if (hasNotificationsEnabled || force) {
      disableProjectNotifications(
        mutationArgs({
          hasNotificationsEnabled: true,
          onCompleted,
          shouldShowToast,
        })
      );
    } else {
      addToast(
        <Toast title={TEXT.notificationsAlreadyDisabled} />,
        TOAST_OPTIONS
      );
      onCompleted?.();
    }
  };

  const enableProjectNotificationsMutation = (
    props?: Omit<MutationArgs, 'force'>
  ) => {
    enableProjectNotifications(
      mutationArgs({
        hasNotificationsEnabled: false,
        ...props,
      })
    );
  };

  return {
    toggleProjectNotificationsMutation,
    disableProjectNotificationsMutation,
    enableProjectNotificationsMutation,
  };
};
